import { useCallback } from 'react';
import moment from 'moment';

import { Nullable, SelectOption } from '~globals/types/commons';
import { CollectItemStateTypes } from '~globals/types/enums';
import { PartnerCompanyItemFilterV2Base } from '~services/partnerCompany/types';
import { AuthExtendedCompany } from '~services/auth/types';
import { DriverCompanyBase } from '~services/driver/types';
import { getItemValue } from '~utils/helpers';
import { formatterDate } from '~utils/formatter';

import { ItemsSearchFilters } from './useItemsSearchFilters';

export const useParseFiltersRequestSearchPartnerCompanies = ({
  filters,
  defaultCollectItemState,
}: {
  filters: ItemsSearchFilters;
  defaultCollectItemState: CollectItemStateTypes[];
}): ((
  currentFilters?: Partial<ItemsSearchFilters>,
) => PartnerCompanyItemFilterV2Base) => {
  const getValueFilter = useCallback(
    <Key extends keyof ItemsSearchFilters>(key: Key): ItemsSearchFilters[Key] =>
      getItemValue(filters, key),
    [filters],
  );

  const parseSearch = useCallback(
    (currenSearch?: string): Nullable<string> => {
      let searchValue = getValueFilter('search');

      if (currenSearch !== undefined) {
        searchValue = currenSearch;
      }

      return searchValue || null;
    },
    [getValueFilter],
  );

  const parseDate = useCallback(
    ({
      currentKey,
      currentDate,
    }: {
      currentKey:
        | 'expirationDateTimeFrom'
        | 'expirationDateTimeTo'
        | 'dateFrom'
        | 'dateTo';
      currentDate?: Nullable<Date>;
    }): Nullable<string> => {
      let dateValue = getValueFilter(currentKey);

      if (currentDate !== undefined) {
        dateValue = currentDate;
      }

      if (!dateValue) return null;

      const formatDate = formatterDate(dateValue, {
        format: moment.HTML5_FMT.DATETIME_LOCAL_SECONDS,
      });

      return formatDate || null;
    },
    [getValueFilter],
  );

  const parseIncludeWithoutMaxDeliveryDatetime = useCallback(
    (includeWithoutExpirationDate?: Nullable<boolean>): Nullable<boolean> => {
      let includeWithoutMaxDeliveryDatetime = getValueFilter(
        'includeWithoutExpirationDate',
      );

      if (includeWithoutExpirationDate !== undefined) {
        includeWithoutMaxDeliveryDatetime = includeWithoutExpirationDate;
      }

      return includeWithoutMaxDeliveryDatetime;
    },
    [getValueFilter],
  );

  const parseGiverCompany = useCallback(
    (currentGiverCompany?: Nullable<AuthExtendedCompany>): Nullable<string> => {
      let giverCompanyValue = getValueFilter('giverCompany');

      if (currentGiverCompany !== undefined) {
        giverCompanyValue = currentGiverCompany;
      }

      return giverCompanyValue?.id ?? null;
    },
    [getValueFilter],
  );

  const parseDriver = useCallback(
    (currentDriver?: Nullable<DriverCompanyBase>): Nullable<string> => {
      let driverValue = getValueFilter('driver');

      if (currentDriver !== undefined) {
        driverValue = currentDriver;
      }

      return driverValue?.id ?? null;
    },
    [getValueFilter],
  );

  const parsCollectItemStates = useCallback(
    (
      currentCollectState?: Nullable<SelectOption[]>,
    ): Nullable<CollectItemStateTypes[]> => {
      let collectItemStateValue = getValueFilter('collectState');

      if (currentCollectState !== undefined) {
        collectItemStateValue = currentCollectState;
      }

      if (!collectItemStateValue || collectItemStateValue.length === 0) {
        return defaultCollectItemState;
      }

      return collectItemStateValue.map((collectItemState) =>
        Number(collectItemState.value),
      );
    },
    [getValueFilter, defaultCollectItemState],
  );

  const parseFiltersRequest = useCallback(
    (
      currentFilters?: Partial<ItemsSearchFilters>,
    ): PartnerCompanyItemFilterV2Base => ({
      routeCode: parseSearch(currentFilters?.search),
      maxDeliveredDateTimeFrom: parseDate({
        currentKey: 'expirationDateTimeFrom',
        currentDate: currentFilters?.expirationDateTimeFrom,
      }),
      maxDeliveredDateTimeTo: parseDate({
        currentKey: 'expirationDateTimeTo',
        currentDate: currentFilters?.expirationDateTimeTo,
      }),
      includeWithoutMaxDeliveryDatetime: parseIncludeWithoutMaxDeliveryDatetime(
        currentFilters?.includeWithoutExpirationDate,
      ),
      dateFrom: parseDate({
        currentKey: 'dateFrom',
        currentDate: currentFilters?.dateFrom,
      }),
      dateTo: parseDate({
        currentKey: 'dateTo',
        currentDate: currentFilters?.dateTo,
      }),
      giverCompanyId: parseGiverCompany(currentFilters?.giverCompany),
      driverId: parseDriver(currentFilters?.driver),
      collectItemStateTypesId: parsCollectItemStates(
        currentFilters?.collectState,
      ),
    }),
    [
      parseSearch,
      parseDate,
      parseIncludeWithoutMaxDeliveryDatetime,
      parseGiverCompany,
      parseDriver,
      parsCollectItemStates,
    ],
  );

  return parseFiltersRequest;
};
